import type { PortalContentKind } from "../../@types/portalAluno";
import { decodeChatbotContentId } from "./chatbotContentLink";
import { SHARED_CONTENT_PARAM } from "./sharedContentLink";

// Tipos de link compartilhado pelo app (https://app-conectado.../<tipo>/<código>) que o portal
// sabe traduzir para uma tela. O link de conteúdo (/conteudo/<código>) tem rota própria.
export const APP_LINK_TYPES = [
  "curso",
  "trilha",
  "comunicado",
  "treinamento-1-ponto",
  "segurancaDoTrabalho",
  "story",
  "carrossel",
] as const;

export type AppLinkType = (typeof APP_LINK_TYPES)[number];

export interface AppLinkTarget {
  to: string;
  state?: { contentType: PortalContentKind };
}

const DETAILS_KIND_BY_TYPE: Partial<Record<AppLinkType, PortalContentKind>> = {
  curso: "cursos",
  trilha: "trilhas",
  comunicado: "comunicados",
  "treinamento-1-ponto": "treinamentos-de-1-ponto",
  segurancaDoTrabalho: "seguranca-do-trabalho",
};

// Nomes usados nos links de carrossel (tela "Links" do painel e tela Listagem do app).
const CAROUSEL_ROUTE_BY_NAME: Record<string, string> = {
  Destaques: "/app/conteudo/destaques",
  SegurancaNoTrabalhos: "/app/conteudo/seguranca-do-trabalho",
  Comunicados: "/app/conteudo/comunicados",
  "Treinamentos de 1 Ponto": "/app/conteudo/treinamentos-de-1-ponto",
  Cursos: "/app/conteudo/cursos",
  Trilhas: "/app/conteudo/trilhas",
  Multimidia: "/app/biblioteca/multimidia",
  Documentos: "/app/biblioteca/documentos",
  Games: "/app/biblioteca/games",
};

export const decodeAppLinkText = (encoded: string) => {
  try {
    const decodedSegment = decodeURIComponent(encoded).trim();
    const normalizedBase64 = decodedSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (normalizedBase64.length % 4)) % 4);

    return window.atob(`${normalizedBase64}${padding}`).trim();
  } catch {
    return undefined;
  }
};

const withSharedFlag = (path: string, params: Record<string, string> = {}) => {
  const search = new URLSearchParams({ ...params, [SHARED_CONTENT_PARAM]: "1" });

  return `${path}?${search.toString()}`;
};

export const resolveAppLinkTarget = (
  type: AppLinkType,
  encoded: string
): AppLinkTarget | undefined => {
  if (type === "carrossel") {
    const name = decodeAppLinkText(encoded);
    const route =
      name && Object.prototype.hasOwnProperty.call(CAROUSEL_ROUTE_BY_NAME, name)
        ? CAROUSEL_ROUTE_BY_NAME[name]
        : undefined;

    return route ? { to: withSharedFlag(route) } : undefined;
  }

  const id = decodeChatbotContentId(encoded);
  if (!id || !Number.isSafeInteger(id)) return undefined;

  if (type === "story") {
    return { to: withSharedFlag("/app", { story: String(id) }) };
  }

  const kind = DETAILS_KIND_BY_TYPE[type];
  if (!kind) return undefined;

  return {
    to: withSharedFlag(`/app/conteudo/detalhes/${kind}/${id}`),
    state: { contentType: kind },
  };
};
